import zipcelx from "zipcelx";
import { readGanttSettings } from "../Gantt/viewSettings";
import axios from "axios";
import {
  EXEC_DATETIME_TIME_RANGE_ID,
  GANTT_TASK_FOCUS_FILTER_EVENT,
  GROUP_TAG,
  PDA_PREFIX,
  REGULARS,
  SCENARIO_TAG,
  TBAR_SPACER,
  PDF_EXPORT_SERVER_HOST,
} from "../../../global/constants";
import { DateHelper } from "@bryntum/core-thin";
import { formatDate } from "../../../utils/DateUtils/DateUtils";
import { triggerEvent } from "../../../utils/ObjectUtils/ObjectUtils";
import { t } from "i18next";
import { exportSchedulerPdfSafely } from "../common/exportService";
import { preferenceStorage } from "../../../services/user-preferences/preferenceStorage";

export const getSettings = (key) => {
  if (!key) return {};
  const savedSettings = preferenceStorage("scheduler_view").get(key, null);
  return savedSettings && typeof savedSettings === "object" ? savedSettings : {};
}; // Need to move it here because of this weird error "cannot access "getSettings" before initialization"

export const resourceColumns = [
  {
    text: t("global.name"),
    field: "name",
    flex: 1,
    filterable: true,
  },
  {
    id: "calendarColumn",
    field: "calendarName",
    text: t("global.calendar"),
    flex: 1,
    editor: false,
    hidden: true,
  },
];

export const defaultSchedulerFeatures = {
  durationDisplayPrecision: 0,
  fillTicks: true, //TODO need to create viewPreset to make fill ticks working
  maxZoomLevel: 16,
  snap: true,
  rowHeight: 50,
  dependenciesFeature: { disabled: true },
  filterFeature: true,
  excelExporterFeature: {
    disabled: false,
    dateFormat: "YYYY-MM-DD HH:mm",
    zipcelx,
  },
  pdfExportFeature: {
    disabled: false,
    exportServer: PDF_EXPORT_SERVER_HOST,
    translateURLsToAbsolute: true,
    sendAsBinary: true,
    // exportSchedulerPdfSafely shows the i18n toast; avoid Bryntum's EN one on top.
    showErrorToast: false,
  },
  resourceNonWorkingTimeFeature: { disabled: false },
  onPresetChange: ({ source }) => {
    const viewPreset = source.viewPreset;
    if (viewPreset.data.id.toLowerCase().includes("day")) {
      source.fillTicks = true;
    } else {
      source.fillTicks = false;
    }
  },
  cellEditFeature: { disabled: true },
  taskEditFeature: {
    disabled: false,
    items: {
      generalTab: {
        items: {
          customDivider: {
            html: "",
            dataset: {
              text: t("global.customFields"),
            },
            cls: "b-divider",
            flex: "1 0 100%",
          },
          deadlineField: {
            type: "datefield",
            name: "deadlineDate",
            label: t("global.deadline"),
            flex: "1",
            cls: "b-inline",
            weight: 710,
          },
          effortField: {
            flex: "1",
          },
          resourcesField: {
            chipView: false,
            multiSelect: false,
          },
          percentDoneField: {
            name: "progress",
          },
        },
      },
      advancedTab: false,
      successorsTab: false,
      predecessorsTab: false,
      resourcesTab: false,
      notesTab: {
        items: {
          noteField: {
            name: "notes",
          },
        },
      },
      /*{
        items: {
          grid: {
            columns: {
              data: {
                lag: false,
                type: false,
              },
            },
          },
        },
      },*/
    },
    editorConfig: {
      autoClose: false,
      bbar: {
        items: {
          deleteButton: false,
        },
      },
    },
  },
  onEventMenuBeforeShow({ eventRecord, items }) {
    const { eventColor } = eventRecord.data;
    items.toggleActive.text = eventColor === "gray" ? t("taskFocus.activate") : t("taskFocus.deactivate");
  },
  nonWorkingTimeFeature: {
    disabled: false,
    hideRangesOnZooming: false,
    maxTimeAxisUnit: "day",
  },
  eventTooltipFeature: {
    template: (data) => {
      let customColumnsHtml = "";
      const customColumns = Object.entries(
        data.eventRecord.data?.customColumns || {}
      );
      customColumns.forEach(([name, value]) => {
        customColumnsHtml += `<div>${name}: ${value}</div>`;
      });

      return `<div>
          <div>${data.eventRecord.data.name}</div>
          <div class="start-block">
            <i class="mr-2 b-fa b-fa-calendar"></i>
            <span>${data.startText}</span>
          </div>
           <div class="end-block">
            <i class="mr-2 b-fa b-fa-calendar"></i>
            <span>${data.endText}</span>
          </div>
          ${customColumnsHtml}
         </div>`;
    },
  },
  timeRangesFeature: {
    showCurrentTimeLine: {
      id: 9999,
      iconCls: "b-fa b-fa-flag",
    },
    showHeaderElements: true,
    enableResizing: false,
    showTooltip: true,
  },
  eventCopyPasteFeature: {
    keyMap: {
      "Ctrl+C": null,
      "Ctrl+X": null,
      "Ctrl+V": null,
    },
  },
};

export const defaultEventMenuItems = {
  copyEvent: false,
  cutEvent: false,
  unassignEvent: false,
  splitEvent: false,
  eventEdit: true,
  editEvent: {
    text: t("global.edit"),
    icon: "b-fa b-fa-pencil-alt",
    onItem: ({ source, eventRecord }) => source.editEvent(eventRecord),
  },
  toggleActive: {
    text: t("taskFocus.toggleActive"),
    icon: "b-fa b-fa-tasks",
    onItem({ eventRecord }) {
      axios
        .patch(`${PDA_PREFIX}/resource-tasks/active/${eventRecord.id}`)
        .then(() => {
          const event = new Event("changeActiveStatus");
          Reflect.set(event, "record", eventRecord);
          document.dispatchEvent(event);
          const eventColor = eventRecord.data.isActive ? "green" : "gray";
          eventRecord.set({
            isActive: !eventRecord.data.isActive,
            inactive: eventRecord.data.isActive,
            eventColor,
          });
        });
    },
  },
  deleteEvent: false,
};

export const schedulerForGanttFeatures = {
  ...defaultSchedulerFeatures,
  id: "scheduler-for-gantt",
  timeRanges: true,
  rowHeight: readGanttSettings().rowHeight || 50,
  barMargin: readGanttSettings().barMargin || 10,
  scheduleMenuFeature: {
    items: {
      addEvent: false,
    },
  },
  eventTooltipFeature: {
    template: (event) => {
      const scheduler = event.tip.owner.grid;
      const gantt = scheduler.partner;
      const eventRecord = event.eventRecord;
      let contentOfParentActivity = "";
      if (eventRecord.parentSAId) {
        const activity = gantt.taskStore.getById(eventRecord.parentSAId);
        let scope = activity.parent;
        while (scope) {
          if (scope.parent.tag === SCENARIO_TAG || scope.parent.tag === GROUP_TAG) break;
          else scope = scope.parent;
        }
        if (activity)
          contentOfParentActivity = `<div>${activity.parent.name} ${scope ? " - " + scope.name : ""}</div>`;
      }
      let customColumnsHtml = "";
      const customColumns = Object.entries(
        eventRecord.data?.customColumns || {}
      );
      customColumns.forEach(([name, value]) => {
        customColumnsHtml += `<div>${name}: ${value}</div>`;
      });

      return `<div>
          <div>${eventRecord.data.name}</div>
          ${contentOfParentActivity}
          <div class="start-block">
            <i class="mr-2 b-fa b-fa-calendar"></i>
            <span>${event.startText}</span>
          </div>
           <div class="end-block">
            <i class="mr-2 b-fa b-fa-calendar"></i>
            <span>${event.endText}</span>
          </div>
          ${customColumnsHtml}
         </div>`;
    },
  }
};

export const wpStatusTemplate = (data) => {
  if (data.genericMetrics.workAssigned === 0.0) {
    return { text: t("taskFocus.wpStatusTemplate.unassigned"), color: "#FFF", mark: "⚪️" };
  } else if (
    data.genericMetrics.workAssigned < data.genericMetrics.workPlanned
  ) {
    return { text: t("taskFocus.wpStatusTemplate.remainingHours"), color: "#fab618aa", mark: "🟡" };
  } else if (
    data.genericMetrics.workAssigned === data.genericMetrics.workPlanned
  ) {
    return { text: t("taskFocus.wpStatusTemplate.fullyAllocated"), color: "#66bb6aaa", mark: "🟢" };
  } else {
    return { text: t("taskFocus.wpStatusTemplate.overBudget"), color: "#fa5252aa", mark: "🔴" };
  }
};

export const getTopBarButtons = (
  scheduler,
  instance,
  additionalButtons = []
) => {
  return [
    {
      icon: "b-icon b-icon-previous",
      tooltip: t('taskFocus.previousTimeSpanTooltip'),
      onAction: () => scheduler.current?.schedulerProInstance.shiftPrevious(),
    },
    {
      icon: "b-icon b-icon-down",
      tooltip: t('taskFocus.goToTodayTooltip'),
      onAction: ({ source }) => {
        const scheduler = source.parent.parent;
        if (scheduler) {
          const today = new Date();
          scheduler.scrollToDate(today, { block: "center" });
        }
      },
    },
    {
      icon: "b-icon b-icon-next",
      tooltip: t('taskFocus.nextTimeSpanTooltip'),
      onAction: () => scheduler.current?.schedulerProInstance.shiftNext(),
    },
    {
      type: "button",
      ref: "zoomInButton",
      icon: "b-icon-search-plus",
      tooltip: t('taskFocus.zoomInTooltip'),
      onAction: () => scheduler.current?.schedulerProInstance.zoomIn(),
    },
    {
      type: "button",
      ref: "zoomOutButton",
      icon: "b-icon b-icon-search-minus",
      tooltip: t('taskFocus.zoomOutTooltip'),
      onAction: () => scheduler.current?.schedulerProInstance.zoomOut(),
    },
    ...(instance === REGULARS
      ? [
        {
          icon: "b-fa b-fa-compress-arrows-alt",
          tooltip: t('taskFocus.zoomToFitTooltip'),
          onClick: () => {
            scheduler.current?.schedulerProInstance.zoomToFit({
              leftMargin: 35,
              rightMargin: 35,
            });
          },
        },
      ]
      : []),
    {
      type: "buttonGroup",
      items: [
        {
          icon: "b-fa b-fa-file-excel",
          tooltip: t('global.exportToExcelTooltip'),
          onClick: () => {
            scheduler.current.schedulerProInstance.features.excelExporter.export(
              {
                filename: t("global.MyLookAheadPlan"),
                exporterConfig: {
                  columns: [{ text: t("global.resources"), field: "name", width: 200 }],
                  eventColumns: [
                    { text: t("global.task"), field: "name", width: 200 },
                    { text: t("global.startDate"), field: "startDate", width: 200 },
                    { text: t("global.endDate"), field: "endDate", width: 200 },
                  ],
                },
              }
            );
          },
        },
        {
          icon: "b-fa b-fa-file-pdf",
          tooltip: t('taskFocus.exportToPdfTooltip'),
          cls: "b-tbar-element",
          onClick: () => {
            exportSchedulerPdfSafely(scheduler.current.schedulerProInstance, { t, name: t("global.MyLookAheadPlan") });
          },
        },
      ],
    },
    ...additionalButtons,
    {
      type: "textfield",
      cls: "filter-by-name",
      flex: "0 0 13.5em",
      align: "end",
      // "Find backlogs..." or "Find tasks..."
      placeholder: t(`workPackageScheduler.placeholderFind.${instance === REGULARS ? "backlogs" : "tasks"}`),
      value: "",
      clearable: true,
      keyStrokeChangeDelay: 100,
      triggers: {
        filter: {
          align: "end",
          cls: "b-fa b-fa-filter",
        },
      },
      onChange: ({ value }) => {
        const activityIds = [];
        if (value === "") {
          scheduler.current?.schedulerProInstance.taskStore.clearFilters();
        } else {
          value = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(value, "i");
          const customColumnFields = scheduler.current.customColumnFields || [];

          scheduler.current?.schedulerProInstance.taskStore.filter({
            filters: (task) => {
              const matchName = task.name && task.name.match(regex);
              const projectName =
                task.projectName && task.projectName.match(regex);
              const matchLinkedResource =
                task.$linkedResources &&
                task.$linkedResources.some(
                  (resource) =>
                    resource.originalData.name &&
                    resource.originalData.name.match(regex)
                );
              const matchCustomColumn = customColumnFields.some((name) =>
                (task.customColumns[name] || "").match(regex)
              );

              const taskIsIncluded =
                matchName ||
                projectName ||
                matchLinkedResource ||
                matchCustomColumn;
              if (taskIsIncluded) activityIds.push(task.id);
              return taskIsIncluded;
            },
            replace: true,
          });
        }
        // Empty value = search cleared → undefined reveals all bottom tasks. A non-empty
        // value keeps activityIds (possibly []) so a zero-match search hides every task.
        triggerEvent(GANTT_TASK_FOCUS_FILTER_EVENT, {
          activityIds: value === "" ? undefined : activityIds,
        });
      },
    },
  ];
};

export const getTopBarButtonsForGanttScheduler = (scheduler) => {
  return [
    TBAR_SPACER,
    {
      type: "textfield",
      cls: "filter-by-name",
      flex: "0 0 13.5em",
      align: "end",
      placeholder: "Find tasks...",
      value: "",
      clearable: true,
      keyStrokeChangeDelay: 100,
      triggers: {
        filter: {
          align: "end",
          cls: "b-fa b-fa-filter",
        },
      },
      onChange: ({ value }) => {
        if (value === "") {
          scheduler.current?.schedulerProInstance.taskStore.clearFilters();
          scheduler.current?.schedulerProInstance.resourceStore.removeFilter("filterResourcesWithEmptyAssignments");
        } else {
          value = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(value, "i");
          const customColumnFields = scheduler.current.customColumnFields;
          const matchingResourceIds = new Set();
          scheduler.current?.schedulerProInstance.taskStore.filter({
            filters: (task) => {
              const matchName = task.name && task.name.match(regex);
              const matchLinkedResource =
                task.$linkedResources &&
                task.$linkedResources.some(
                  (resource) =>
                    resource.originalData.name &&
                    resource.originalData.name.match(regex)
                );
              const matchCustomColumn = customColumnFields.some((name) =>
                (task.customColumns[name] || "").match(regex)
              );
              const matchAcronym = task.acronym && task.acronym.match(regex);
              const matchNotes = task.notes && task.notes.match(regex);

              return (
                matchName ||
                matchLinkedResource ||
                matchCustomColumn ||
                matchAcronym ||
                matchNotes
              );
            },
            replace: true,
          });
          scheduler.current?.schedulerProInstance.taskStore.forEach(task => {
            task.assignments.forEach(assignment => matchingResourceIds.add(assignment.resourceId));
          });
          setTimeout(() => {
            scheduler.current?.schedulerProInstance.resourceStore.filter({
              id: "filterResourcesWithEmptyAssignments",
              filterBy: (resource) => matchingResourceIds.has(resource.id),
            });
          }, 50);
        }
      },
    },
  ];
};

export const timeAxis = {
  zoomLevels: [
    {
      unit: "minute",
      increment: 5,
      resolution: {
        unit: "second",
        increment: 1,
      },
    },
    {
      unit: "hour",
      increment: 1,
      resolution: {
        unit: "minute",
        increment: 10,
      },
    },
    {
      unit: "day",
      increment: 1,
      resolution: {
        unit: "hour",
        increment: 1,
      },
    },
  ],
};

export const viewPresetExample = {
  timeResolution: {
    unit: "day",
    increment: 1,
  },

  headers: [
    {
      unit: "month",
      dateFormat: "MMM YYYY",
      align: "start",
    },
    {
      unit: "week",
      renderer: (startDate, endDate) =>
        `Week ${DateHelper.format(startDate, "WW")}`,
    },
    {
      unit: "day",
      dateFormat: "dd",
    },
    {
      unit: "day",
      dateFormat: "DD",
    },
  ],
};

export const defaultCalendar = {
  id: "defaultCalendar",
  name: "Default calendar",
  unspecifiedTimeIsWorking: false,
  intervals: [
    {
      recurrentStartDate: "On MON at 09:00",
      recurrentEndDate: "On MON at 16:00",
      isWorking: true,
    },
    {
      recurrentStartDate: "On TUE at 09:00",
      recurrentEndDate: "On TUE at 16:00",
      isWorking: true,
    },
    {
      recurrentStartDate: "On WED at 09:00",
      recurrentEndDate: "On WED at 16:00",
      isWorking: true,
    },
    {
      recurrentStartDate: "On THU at 09:00",
      recurrentEndDate: "On THU at 16:00",
      isWorking: true,
    },
    {
      recurrentStartDate: "On FRI at 09:00",
      recurrentEndDate: "On FRI at 16:00",
      isWorking: true,
    },
  ],
};

export const schedulerTimeRanges = (start, finish) => [
  {
    id: 1,
    name: t("global.filterStart"),
    iconCls: "b-fa b-fa-calendar",
    startDate: start,
    duration: 0,
    durationUnit: "day",
  },
  {
    id: 2,
    name: t("global.filterEnd"),
    iconCls: "b-fa b-fa-calendar",
    startDate: finish,
    duration: 0,
    durationUnit: "day",
  },
];

export const schedulerExecDateTimeRanges = (execDateTime) => [
  {
    duration: 1,
    durationUnit: "day",
    id: EXEC_DATETIME_TIME_RANGE_ID,
    name: "Exec date start",
    startDate: formatDate(execDateTime),
  },
];
